import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LeadService } from '@/services/lead/leadService';
import { BaseLead, LeadDetails } from '@/types/lead';
import { ValuationService } from '@/services/valuation/valuationService';

export const useLead = () => {
  const queryClient = useQueryClient();
  
  /**
   * Get all leads query with dynamic parameters
   */
  const useGetAllLeads = (
    page: number = 1,
    limit: number = 10,
    statuses?: string[],
    vehicleType?: string,
    search?: string
  ) => useQuery({
    queryKey: ['leads', page, limit, statuses, vehicleType, search],
    queryFn: () => LeadService.getAllLeads(page, limit, statuses, vehicleType, search),
    // Admin data must always reflect the backend (user ruling 2026-09-20):
    // nothing here is ever served from cache.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  /**
   * Get single lead query
   */
  const useGetLead = (leadId: string) => useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => LeadService.getLead(leadId),
    enabled: !!leadId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });

  /**
   * Update lead status mutation
   */
  const updateLeadStatusMutation = useMutation({
    mutationFn: ({ leadId, status }: { leadId: string; status: string }) => 
      LeadService.updateLeadStatus(leadId, status),
    onSuccess: (_, variables) => {
      // Invalidate the specific lead and leads list
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  /**
   * Add comment to lead mutation
   */
  const addCommentMutation = useMutation({
    mutationFn: ({ leadId, comment }: { leadId: string; comment: { text: string; author: string; mentions?: string[] } }) => 
      LeadService.addComment(leadId, comment),
    onSuccess: (_, variables) => {
      // Invalidate the specific lead to refetch with new comment
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: ({ leadId, commentId, text }: { leadId: string; commentId: string; text: string }) =>
      LeadService.updateComment(leadId, commentId, text),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: ({ leadId, commentId }: { leadId: string; commentId: string }) =>
      LeadService.deleteComment(leadId, commentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  /**
   * Mask lead mutation (for guest leads)
   */
  const maskLeadMutation = useMutation({
    mutationFn: (leadId: string) => LeadService.maskLead(leadId),
    onSuccess: (response, leadId) => {
      // Update cache directly with the returned data to ensure immediate UI update with correct status
      if (response.data) {
        queryClient.setQueryData(['lead', leadId], response.data);
      }
      // Invalidate queries to ensure fresh data on next fetch
      queryClient.invalidateQueries({ queryKey: ['lead', leadId], exact: false });
      queryClient.invalidateQueries({ queryKey: ['leads'], exact: false });
    },
  });

  /**
   * Fetch rating intervals for a lead from AutoUncle (admin "Uppdatera priser").
   * Mirrors fetchRatingIntervalsMutation in useVehicleCard so both refresh panels
   * behave identically.
   */
  const fetchRatingIntervalsMutation = useMutation({
    mutationFn: ({ leadId }: { leadId: string }) =>
      ValuationService.fetchLeadRatingIntervals(leadId),
    onSuccess: (_data, variables) => {
      // The refresh also rewrites the stored headline price (market/purchase price),
      // which is rendered from the lead query - not from the mutation result - so the
      // lead has to be refetched for it to re-render.
      queryClient.invalidateQueries({ queryKey: ['lead', variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'], exact: false });
    },
  });

  return {
    useGetAllLeads,
    useGetLead,
    updateLeadStatusMutation,
    updateLeadStatusAsync: updateLeadStatusMutation.mutateAsync,
    isUpdatingLeadStatus: updateLeadStatusMutation.isPending,
    updateLeadStatusError: updateLeadStatusMutation.error,
    addCommentMutation,
    addCommentAsync: addCommentMutation.mutateAsync,
    isAddingComment: addCommentMutation.isPending,
    addCommentError: addCommentMutation.error,

    updateCommentAsync: updateCommentMutation.mutateAsync,
    isUpdatingComment: updateCommentMutation.isPending,
    updateCommentError: updateCommentMutation.error,

    deleteCommentAsync: deleteCommentMutation.mutateAsync,
    isDeletingComment: deleteCommentMutation.isPending,
    deleteCommentError: deleteCommentMutation.error,
    maskLeadMutation,
    maskLeadAsync: maskLeadMutation.mutateAsync,
    isMaskingLead: maskLeadMutation.isPending,
    maskLeadError: maskLeadMutation.error,

    fetchRatingIntervalsMutation,
    fetchRatingIntervalsAsync: fetchRatingIntervalsMutation.mutateAsync,
    isFetchingRatingIntervals: fetchRatingIntervalsMutation.isPending,
    fetchRatingIntervalsError: fetchRatingIntervalsMutation.error,
  };
};

